<script setup>
/**
 * KaptionChatPreferencesPanel
 *
 * The body of the "Chat preferences" popover. Rendered both from the
 * conversation list header and from the reply box, so an agent can reach
 * these settings from where they matter instead of from inside a filter menu.
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import {
  useKaptionChatPreferences,
  UNDO_DURATION_OPTIONS,
} from 'dashboard/composables/useKaptionChatPreferences';

const { t } = useI18n();
const {
  KAPTION_DELIVERY_MODES,
  KAPTION_MERGE_LEVELS,
  messageMergingEnabled,
  mergeLevels,
  mergeLevel,
  mergeLevelLabel,
  mergeLocked,
  accountMergeDefault,
  undoEnabled,
  showSendSettings,
  showLiveTranslationSetting,
  showWriteNotesSetting,
  deliveryMode,
  deliveryModes,
  deliveryModeLabel,
  undoDuration,
  enterToSend,
  enterToSendHint,
  liveTranslation,
  writeNotesDefault,
  languages,
  selectedLanguage,
  accountDefaultLocale,
} = useKaptionChatPreferences();

const isLanguageListOpen = ref(false);

function selectLanguage(code) {
  selectedLanguage.value = code;
  isLanguageListOpen.value = false;
}

function currentLanguageName() {
  const match = languages.value.find(l => l.code === selectedLanguage.value);
  return match?.name || t('KAPTION_PLATFORM.SETTINGS.LANGUAGE_DEFAULT');
}
</script>

<template>
  <div class="flex flex-col w-80 py-2" data-testid="kaption-chat-preferences">
    <!-- Language list replaces the panel body while open -->
    <template v-if="isLanguageListOpen">
      <button
        type="button"
        class="flex items-center gap-1 px-4 py-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
        @click="isLanguageListOpen = false"
      >
        <span class="i-lucide-chevron-left size-3.5" />
        {{ t('KAPTION_PLATFORM.SETTINGS.LANGUAGE') }}
      </button>
      <button
        v-for="lang in languages"
        :key="lang.code || 'default'"
        type="button"
        class="flex items-center gap-3 px-4 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2 font-medium': selectedLanguage === lang.code }"
        @click="selectLanguage(lang.code)"
      >
        <span
          v-if="lang.isDefault"
          class="i-lucide-globe size-4 text-n-slate-11"
        />
        <KaptionCircleFlag v-else :language-code="lang.code" size="size-4" />
        <span class="flex-1">{{ lang.name }}</span>
        <span v-if="lang.isDefault" class="text-xs text-n-slate-11">
          {{ accountDefaultLocale }}
        </span>
        <span
          v-if="selectedLanguage === lang.code"
          class="i-lucide-check size-4 text-n-teal-10"
        />
      </button>
    </template>

    <template v-else>
      <h3 class="px-4 pt-1 pb-2 text-sm font-semibold text-n-slate-12">
        {{ t('KAPTION_PLATFORM.PREFERENCES.TITLE') }}
      </h3>

      <!-- Sending -->
      <p
        class="flex items-center gap-1.5 px-4 pt-2 pb-1.5 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
      >
        <span class="i-lucide-send size-3.5" />
        {{ t('KAPTION_PLATFORM.PREFERENCES.SENDING') }}
      </p>

      <!-- whatsapp-message-merging: the three levels + Undo as its own toggle -->
      <template v-if="showSendSettings && messageMergingEnabled">
        <p class="px-4 pb-1 text-sm font-medium text-n-slate-12">
          {{ t('KAPTION_MESSAGE_MERGE.SETTINGS.TITLE') }}
        </p>
        <div
          role="radiogroup"
          :aria-label="t('KAPTION_MESSAGE_MERGE.SETTINGS.TITLE')"
          class="flex gap-1 p-1 mx-4 mb-1 rounded-lg bg-n-alpha-2"
          data-testid="preferences-merge-level"
        >
          <button
            v-for="level in mergeLevels"
            :key="level"
            type="button"
            role="radio"
            :aria-checked="mergeLevel === level"
            :disabled="mergeLocked"
            :data-testid="`preferences-merge-${level}`"
            class="flex-1 h-7 px-2 text-xs rounded-md whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-60"
            :class="
              mergeLevel === level
                ? 'bg-n-solid-active text-n-slate-12 font-semibold shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="mergeLevel = level"
          >
            {{ mergeLevelLabel(level) }}
          </button>
        </div>
        <p class="px-4 pb-1 text-xs text-n-slate-11">
          {{ t(`KAPTION_MESSAGE_MERGE.SETTINGS.DESCRIPTIONS.${mergeLevel}`) }}
          <template v-if="mergeLevel !== KAPTION_MERGE_LEVELS.OFF">
            {{ t('KAPTION_MESSAGE_MERGE.SETTINGS.ENTER_HINT') }}
          </template>
        </p>
        <p
          class="flex items-center gap-1.5 px-4 pb-1.5 text-xs text-n-slate-11"
          data-testid="preferences-merge-default"
        >
          <span v-if="mergeLocked" class="i-lucide-lock size-3" />
          {{
            mergeLocked
              ? t('KAPTION_MESSAGE_MERGE.SETTINGS.LOCKED', {
                  level: mergeLevelLabel(accountMergeDefault),
                })
              : t('KAPTION_MESSAGE_MERGE.SETTINGS.ACCOUNT_DEFAULT', {
                  level: mergeLevelLabel(accountMergeDefault),
                })
          }}
        </p>
        <label
          class="flex items-center gap-3 px-4 py-1.5 cursor-pointer"
          data-testid="preferences-undo-toggle"
        >
          <span class="flex flex-col flex-1 min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ t('KAPTION_MESSAGE_MERGE.SETTINGS.UNDO_TOGGLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('KAPTION_MESSAGE_MERGE.SETTINGS.UNDO_TOGGLE_HINT') }}
            </span>
          </span>
          <Switch v-model="undoEnabled" />
        </label>
        <div v-if="undoEnabled" class="flex items-center gap-3 px-4 py-1.5">
          <span class="flex-1 text-sm text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS') }}
          </span>
          <div
            class="flex gap-0.5 p-0.5 rounded-lg bg-n-alpha-2"
            role="group"
            :aria-label="t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS')"
          >
            <button
              v-for="duration in UNDO_DURATION_OPTIONS"
              :key="duration"
              type="button"
              :aria-pressed="undoDuration === duration"
              class="h-6 px-2 text-xs rounded-md"
              :class="
                undoDuration === duration
                  ? 'bg-n-solid-active text-n-slate-12 font-semibold shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="undoDuration = duration"
            >
              {{
                t('KAPTION_PLATFORM.SETTINGS.UNDO_MODE.DURATION_OPTION', {
                  seconds: duration,
                })
              }}
            </button>
          </div>
        </div>
      </template>

      <template v-else-if="showSendSettings">
        <div
          role="radiogroup"
          :aria-label="t('KAPTION_PLATFORM.SETTINGS.DELIVERY.TITLE')"
          class="flex flex-wrap gap-1 p-1 mx-4 mb-1 rounded-lg bg-n-alpha-2"
          data-testid="preferences-delivery-mode"
        >
          <button
            v-for="mode in deliveryModes"
            :key="mode"
            type="button"
            role="radio"
            :aria-checked="deliveryMode === mode"
            :data-testid="`preferences-delivery-${mode}`"
            class="flex-1 h-7 px-2 text-xs rounded-md whitespace-nowrap"
            :class="
              deliveryMode === mode
                ? 'bg-n-solid-active text-n-slate-12 font-semibold shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="deliveryMode = mode"
          >
            {{ deliveryModeLabel(mode) }}
          </button>
        </div>
        <div
          v-if="deliveryMode === KAPTION_DELIVERY_MODES.UNDO"
          class="flex items-center gap-3 px-4 py-1.5"
        >
          <span class="flex-1 text-sm text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS') }}
          </span>
          <div
            class="flex gap-0.5 p-0.5 rounded-lg bg-n-alpha-2"
            role="group"
            :aria-label="t('KAPTION_PLATFORM.SETTINGS.DELIVERY.UNDO_SECONDS')"
          >
            <button
              v-for="duration in UNDO_DURATION_OPTIONS"
              :key="duration"
              type="button"
              :aria-pressed="undoDuration === duration"
              class="h-6 px-2 text-xs rounded-md"
              :class="
                undoDuration === duration
                  ? 'bg-n-solid-active text-n-slate-12 font-semibold shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="undoDuration = duration"
            >
              {{
                t('KAPTION_PLATFORM.SETTINGS.UNDO_MODE.DURATION_OPTION', {
                  seconds: duration,
                })
              }}
            </button>
          </div>
        </div>
      </template>

      <label
        class="flex items-center gap-3 px-4 py-1.5 cursor-pointer"
        data-testid="preferences-enter-to-send"
      >
        <span class="flex flex-col flex-1 min-w-0">
          <span class="text-sm text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SETTINGS.ENTER_TO_SEND.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">{{ enterToSendHint }}</span>
        </span>
        <Switch v-model="enterToSend" />
      </label>

      <label
        v-if="showWriteNotesSetting"
        class="flex items-center gap-3 px-4 py-1.5 cursor-pointer"
      >
        <span class="flex flex-col flex-1 min-w-0">
          <span class="text-sm text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SETTINGS.WRITE_NOTES.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('KAPTION_PLATFORM.SETTINGS.WRITE_NOTES.DESCRIPTION') }}
          </span>
        </span>
        <Switch v-model="writeNotesDefault" />
      </label>

      <!-- AI -->
      <template v-if="showLiveTranslationSetting">
        <div class="h-px mx-4 my-2 bg-n-weak" />
        <p
          class="flex items-center gap-1.5 px-4 pt-1 pb-1.5 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
        >
          <span class="i-lucide-sparkles size-3.5 text-n-copilot-9" />
          {{ t('KAPTION_PLATFORM.PREFERENCES.AI') }}
        </p>
        <label
          v-if="showLiveTranslationSetting"
          class="flex items-center gap-3 px-4 py-1.5 cursor-pointer"
        >
          <span class="flex flex-col flex-1 min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ t('KAPTION_PLATFORM.SETTINGS.LIVE_TRANSLATION.TITLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('KAPTION_PLATFORM.SETTINGS.LIVE_TRANSLATION.DESCRIPTION') }}
            </span>
          </span>
          <Switch v-model="liveTranslation" />
        </label>
      </template>

      <!-- Language -->
      <div class="h-px mx-4 my-2 bg-n-weak" />
      <button
        type="button"
        class="flex items-center gap-3 px-4 py-2 text-left hover:bg-n-alpha-2"
        @click="isLanguageListOpen = true"
      >
        <span class="flex-1 text-sm text-n-slate-12">
          {{ t('KAPTION_PLATFORM.SETTINGS.LANGUAGE') }}
        </span>
        <span class="text-xs text-n-slate-11">{{ currentLanguageName() }}</span>
        <span class="i-lucide-chevron-right size-4 text-n-slate-10" />
      </button>
    </template>
  </div>
</template>
